import { useState } from "react";
import { Button, Dialog, EmptyState, Segmented, useLayout, useToast } from "@agentsaas/ui";
import { useErrorText, useT } from "../app/i18n";
import { Loading } from "../layout/Loading";
import { CodeEditor, DiffView } from "./CodeEditor";
import { useFiles } from "./FilesContext";
import { isDirty, type OpenFile } from "./store";
import { isModelFile } from "../assets/viewer-model";
import { ModelFileView } from "./ModelFileView";

/** 单个已打开文件的视图：编辑器 / diff、外部修改提示、保存与冲突处理 */
export function FileView({ file }: { file: OpenFile }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { store } = useFiles();
  const mobile = useLayout() === "mobile";
  const [conflictOpen, setConflictOpen] = useState(false);
  // 手机默认只读，点"编辑"后可以轻量编辑
  const [mobileEdit, setMobileEdit] = useState(false);
  // P8：模型文件默认用 3D 查看器打开；文本格式（.gltf / .obj）可切到源码
  const [model3d, setModel3d] = useState(true);
  const dirty = isDirty(file);

  const save = async () => {
    try {
      const r = await store.save(file.path);
      if (r === "conflict") setConflictOpen(true);
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    }
  };
  const resolve = async (c: "overwrite" | "discard" | "diff") => {
    setConflictOpen(false);
    try {
      await store.resolveConflict(file.path, c);
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    }
  };

  if (file.loading) return <Loading />;
  if (file.error) return <EmptyState icon="file" title={errText(file.error)} />;
  if (isModelFile(file.path) && (file.binary || model3d)) return <ModelFileView file={file} onSource={file.binary ? undefined : () => setModel3d(false)} />;
  if (file.binary)
    return (
      <EmptyState icon="file" title={t("fs.binary")}>
        {file.binary.url ? (
          <a href={file.binary.url} target="_blank" rel="noreferrer">
            {t("fs.download")}
          </a>
        ) : null}
      </EmptyState>
    );

  return (
    <div className="fileview" data-testid="file-view" data-path={file.path}>
      <div className="fileview__bar">
        <span className="ellipsis grow small muted" title={file.path}>
          {file.path}
          {dirty ? <span className="fileview__dirty">{` · ${t("fs.unsaved")}`}</span> : null}
        </span>
        {isModelFile(file.path) ? (
          <Segmented
            label={t("model3d.file.mode")}
            value="source"
            onChange={(v) => v === "3d" && setModel3d(true)}
            options={[
              { value: "3d", label: t("model3d.file.view_3d") },
              { value: "source", label: t("model3d.file.view_source") },
            ]}
          />
        ) : null}
        {mobile ? (
          <Segmented
            label={t("fs.edit")}
            value={mobileEdit ? "edit" : "ro"}
            onChange={(v) => setMobileEdit(v === "edit")}
            options={[
              { value: "ro", label: t("fs.readonly") },
              { value: "edit", label: t("fs.edit") },
            ]}
          />
        ) : null}
        <Button size="sm" variant="ghost" icon="diff" aria-pressed={file.view === "diff"} onClick={() => store.setView(file.path, file.view === "diff" ? "edit" : "diff")} data-testid="toggle-diff">
          {file.view === "diff" ? t("fs.back_to_edit") : t("fs.diff")}
        </Button>
        <Button size="sm" variant="primary" icon="save" loading={file.saving} disabled={!dirty && file.external !== "deleted"} onClick={() => void save()} data-testid="save-file">
          {t("common.save")}
        </Button>
      </div>
      {file.external ? (
        <div className="banner banner--warning" role="status" data-testid="external-banner">
          <span className="grow">{file.external === "deleted" ? t("fs.external.deleted") : t("fs.external.changed")}</span>
          {file.external !== "deleted" ? (
            <>
              <Button size="sm" onClick={() => store.compareExternal(file.path)}>
                {t("fs.external.compare")}
              </Button>
              <Button size="sm" onClick={() => store.acceptExternal(file.path)}>
                {t("fs.external.reload")}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {file.view === "diff" ? <div className="fileview__hint small muted">{t("fs.diff_hint")}</div> : null}
      <div className="fileview__editor">
        {file.view === "diff" ? (
          <DiffView path={file.path} original={file.content} modified={file.draft} onChange={(v) => store.setDraft(file.path, v)} />
        ) : (
          <CodeEditor path={file.path} value={file.draft} onChange={(v) => store.setDraft(file.path, v)} onSave={() => void save()} readOnly={mobile && !mobileEdit} />
        )}
      </div>
      <Dialog
        open={conflictOpen}
        onClose={() => setConflictOpen(false)}
        title={t("fs.conflict.title")}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => void resolve("diff")} data-testid="conflict-diff">
              {t("fs.conflict.view_diff")}
            </Button>
            <Button onClick={() => void resolve("discard")} data-testid="conflict-discard">
              {t("fs.conflict.discard")}
            </Button>
            <Button variant="danger" onClick={() => void resolve("overwrite")} data-testid="conflict-overwrite">
              {t("fs.conflict.overwrite")}
            </Button>
          </>
        }
      >
        <p>{t("fs.conflict.body", { name: file.name })}</p>
      </Dialog>
    </div>
  );
}
